<template>
	<Link
		ref="cardRef"
		:href="props.package.link"
		:alt="`See more about the ${props.section} ${priceType} package`"
		class="shadow lg:shadow-lg rounded group cursor-pointer top-0 hover:-top-4 transition-all ease-in-out relative overflow-hidden"
		:class="{
			'scale-110 z-10': isRecommended && mq.lgPlus,
			'order-1': mq.mdMinus && isRecommended,
			'order-2': mq.mdMinus && !isRecommended,
		}"
	>
		<div class="flex flex-col items-center h-full">
			<!-- BANNER -->
			<div v-if="isRecommended" class="absolute bg-secondary-900 text-white px-8 py-1 uppercase">Recommended</div>
			<!-- UPPER SECTION -->
			<div class="flex-grow bg-white w-full">
				<div class="text-center px-8 mt-12 mb-2">
					<h3 class="text-3xl lg:mb-4">{{ priceType }}</h3>
					<p class="text-xl text-dark-500 leading-tight mb-8">{{ props.package.summary }}</p>
					<span v-if="props.package.acf.use_from_price" class="text-xs uppercase">From</span>

					<p v-if="priceType === 'Bespoke'" class="text-5xl font-semibold">POA</p>
					<p v-else>
						<span class="text-4xl font-semibold">£{{ formattedPrice }}</span>
						<span class="text-xs">+VAT</span>
					</p>
					<p class="text-dark-500">{{ props.package.acf.additional_price }}</p>
					<p class="text-dark-500">{{ props.package.acf.additional_price_subheading }}</p>
				</div>
			</div>
			<!-- LOWER SECTION -->
			<div class="w-full bg-white">
				<div
					ref="trimRef"
					class="transition-transform duration-300 origin-top-right -ml-8 rotate-[-8deg] bg-dark-900 h-28 sm:h-28 md:h-32 lg:h-20"
				>
					<div
						class="z-50 trim-gradient bg-gradient-to-r from-primary-900 via-secondary-900 to-primary-900 h-3 w-full"
						:class="{
							'animated-trim': isRecommended,
						}"
					></div>
				</div>
				<div v-if="mq.lgPlus" class="bg-dark-900 w-full text-white px-16 pb-4 rounded-b relative">
					<ul role="list" class="pb-4 marker:text-secondary-900 list-disc space-y-2 relative text-sm">
						<li v-for="(feature, index) in props.package.acf.features" :key="index">
							{{ feature.feature }}
						</li>
					</ul>
					<Transition name="fade">
						<div
							v-if="isOutside === false"
							class="absolute inset-0 px-8 text-lg bg-dark-900 flex items-center justify-center"
						>
							<div>
								For more information about the
								<span class="font-semibold">{{ props.section }} {{ priceType }}</span> package, please
								click this card.
							</div>
						</div>
					</Transition>
				</div>
			</div>
		</div>
	</Link>
</template>

<script setup>
import { useMq } from "vue3-mq";
import { Link } from "@inertiajs/vue3";
import { useMouseInElement } from "@vueuse/core";

let cardRef = ref(null);
let trimRef = ref(null);
const mq = useMq();
const props = defineProps({
	package: {
		type: Object,
		default: () => ({}),
	},
	section: {
		type: String,
		default: "",
	},
});

const { isOutside } = useMouseInElement(cardRef);

const priceType = computed(
	() =>
		({
			"monthly-plus": "Monthly+",
			monthly: "Monthly",
			upfront: "Upfront",
			bespoke: "Bespoke",
		}[props.package.acf.price_type])
);

const isRecommended = computed(() => !!props.package.acf.recommended);
const formattedPrice = computed(() => {
	const pr = +props.package.acf.price;
	if (isNaN(pr)) return props.package.acf.price;
	else return pr.toLocaleString();
});
</script>

<style lang="postcss">
.trim-gradient {
	background-size: 200%;
}
.animated-trim {
	animation: 5s gradient linear infinite;
}

@keyframes gradient {
	0% {
		background-position: 0% 0%;
	}
	100% {
		background-position: -200% 0%;
	}
}
</style>
